import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, coverUrl } from "../lib/api";

export default function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [productsById, setProductsById] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/bundles"),
      api.get("/products", { params: { limit: 200 } }),
    ]).then(([b, p]) => {
      setBundles(b.data || []);
      const items = (p.data && (p.data.items || p.data)) || [];
      const map = {};
      for (const x of items) map[x.id] = x;
      setProductsById(map);
    }).finally(() => setLoading(false));
  }, []);

  const enriched = useMemo(() => bundles.map((b) => {
    const members = (b.product_ids || []).map((pid) => productsById[pid]).filter(Boolean);
    const sumOriginal = members.reduce((s, m) => s + (m.price || 0), 0);
    const savings = Math.max(0, sumOriginal - (b.price || 0));
    return { ...b, members, sumOriginal, savings };
  }), [bundles, productsById]);

  return (
    <div className="container-page py-16 max-w-6xl" data-testid="bundles-page">
      <div className="border-b border-border pb-6 mb-10">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground italic mb-3">Collected reading</div>
        <h1 className="font-display text-5xl tracking-tight">Bundles</h1>
        <p className="text-base text-muted-foreground italic mt-3 max-w-2xl leading-relaxed">
          Curated sets of our titles, grouped by season or mood and priced together to save versus buying separately.
          Every bundle is a single one-time purchase — once you have it, every title appears in your library forever.
        </p>
      </div>

      {loading && <div className="text-sm text-muted-foreground italic">Loading…</div>}

      {!loading && enriched.length === 0 && (
        <div className="text-base text-muted-foreground italic py-16 text-center">
          No bundles available right now — please check back soon.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-10">
        {enriched.map((b) => (
          <article
            key={b.id}
            className="border border-border rounded-sm bg-card overflow-hidden flex flex-col"
            data-testid={`bundle-card-${b.id}`}
          >
            {/* Stacked covers */}
            <div className="relative bg-muted/40 aspect-[16/9] flex items-center justify-center overflow-hidden">
              <div className="flex items-center justify-center gap-[-12px] py-6">
                {b.members.slice(0, 4).map((m, i) => (
                  <div
                    key={m.id}
                    className="relative shadow-[0_8px_24px_-10px_rgba(58,31,71,0.45)] border border-border/60 bg-muted"
                    style={{
                      width: "112px",
                      height: "164px",
                      marginLeft: i === 0 ? 0 : "-28px",
                      transform: `rotate(${(i - (b.members.length - 1) / 2) * 4}deg)`,
                      zIndex: 10 - i,
                    }}
                  >
                    <img
                      loading="lazy"
                      src={coverUrl(m)}
                      alt={m.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              {b.savings > 0 && (
                <div className="absolute top-3 right-3 bg-accent text-accent-foreground rounded-sm px-2.5 py-1 text-[11px] font-medium tracking-wide" data-testid={`bundle-savings-${b.id}`}>
                  Save A${b.savings.toFixed(2)}
                </div>
              )}
            </div>

            <div className="p-6 flex-1 flex flex-col">
              <h2 className="font-display text-2xl leading-tight">{b.title}</h2>
              {b.description && (
                <p className="text-sm text-muted-foreground italic mt-2 leading-relaxed line-clamp-3">
                  {b.description}
                </p>
              )}

              {/* Member list */}
              {b.members.length > 0 && (
                <ul className="mt-4 space-y-1.5 text-sm">
                  {b.members.map((m) => (
                    <li key={m.id} className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-1">
                      <Link
                        to={`/product/${m.id}`}
                        className="text-foreground/85 hover:text-accent transition-colors flex-1 line-clamp-1"
                      >
                        {m.title}
                      </Link>
                      <span className="text-xs text-muted-foreground tabular-nums">A${m.price.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6 flex items-end justify-between gap-4 pt-4 border-t border-border">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Bundle price</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-display text-3xl">A${b.price.toFixed(2)}</span>
                    {b.savings > 0 && (
                      <span className="text-sm text-muted-foreground line-through tabular-nums">A${b.sumOriginal.toFixed(2)}</span>
                    )}
                  </div>
                </div>
                <a
                  href="mailto:hello@softspinepress.com?subject=Bundle%20order"
                  className="inline-flex items-center px-5 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                  data-testid={`bundle-enquire-${b.id}`}
                >
                  Email to order
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
